import { useCallback, useState } from "react"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { SharedAddConnectionDialog, AddConnectionSubmitPayload } from "@hydrologiq/shared/data-connections/AddConnectionDialog"
import { AvailableConnection, CreateDataConnectionInput } from "../../hooks/useDataConnections"
import { useUnlinkedInstances } from "../../hooks/useUnlinkedInstances"

export interface AddConnectionDialogProps {
  systemId: string
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  availableConnections: AvailableConnection[]
  loadingAvailable: boolean
  onCreateConnection: (input: CreateDataConnectionInput) => Promise<void>
  creating: boolean
}

export function AddConnectionDialog({
  systemId,
  isOpen,
  onClose,
  onCreated,
  availableConnections,
  loadingAvailable,
  onCreateConnection,
  creating,
}: AddConnectionDialogProps) {
  const [selectedMonitorId, setSelectedMonitorId] = useState("")
  const { unlinkedInstances, loadingUnlinked } = useUnlinkedInstances(selectedMonitorId)

  const handleSubmit = useCallback(
    async (payload: AddConnectionSubmitPayload) => {
      try {
        await onCreateConnection({
          systemId,
          monitorId: payload.monitorId,
          externalId: payload.externalId,
          deviceModel: payload.deviceModel,
        })
        toaster.success({ title: "Data connection created" })
        onCreated()
      } catch (err: any) {
        const message = err?.message || "Failed to create data connection"
        toaster.error({ title: "Error", description: message })
        throw err
      }
    },
    [systemId, onCreateConnection, onCreated],
  )

  return (
    <SharedAddConnectionDialog
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      connectors={availableConnections}
      loadingConnectors={loadingAvailable}
      unlinkedInstances={unlinkedInstances}
      loadingUnlinked={loadingUnlinked}
      onConnectorChange={setSelectedMonitorId}
      creating={creating}
      showConnectorTypeSelect={true}
    />
  )
}

export default AddConnectionDialog
